import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { apiFetch, v1Path } from '../../lib/api';
import type { LabelDto, MemberDto, StateDto, WorkItemSlim } from '../../lib/work-types';
import { useOverlays } from './overlay-context';

export type BulkSelection = Map<string, { id: string; version: number }>;

type Props = {
  projectId: string;
  selection: BulkSelection;
  onClear: () => void;
  onApplied: () => void;
};

export default function BulkBar({ projectId, selection, onClear, onApplied }: Props) {
  const { toast, confirm } = useOverlays();
  const [states, setStates] = useState<StateDto[]>([]);
  const [labels, setLabels] = useState<LabelDto[]>([]);
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [busy, setBusy] = useState(false);
  const items = Array.from(selection.values());

  useEffect(() => {
    if (items.length === 0) return;
    void Promise.all([
      apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`)),
      apiFetch<{ items: LabelDto[] }>(v1Path(`/projects/${projectId}/labels`)),
      apiFetch<{ items: MemberDto[] }>(v1Path('/members?limit=100&offset=0')),
    ])
      .then(([s, l, m]) => {
        setStates(s.items);
        setLabels(l.items);
        setMembers(m.items);
      })
      .catch((err: unknown) => {
        toast(err instanceof Error ? err.message : 'Failed to load bulk options');
      });
  }, [projectId, items.length, toast]);

  if (items.length === 0) return null;

  async function apply(set: Record<string, unknown>) {
    setBusy(true);
    try {
      await apiFetch<{ items: WorkItemSlim[] }>(v1Path(`/projects/${projectId}/work-items/bulk`), {
        method: 'POST',
        body: JSON.stringify({
          items: items.map((i) => ({ id: i.id, version: i.version })),
          set,
        }),
      });
      toast(`Updated ${items.length} item${items.length === 1 ? '' : 's'}`);
      onApplied();
      onClear();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    const ok = await confirm({
      title: 'Mark selected as done?',
      description: 'They leave the open lists and show on Completed. You can reopen them.',
      confirmLabel: 'Mark done',
    });
    if (!ok) return;
    await apply({ archived: true });
  }

  return (
    <div className="sticky bottom-0 z-30 flex flex-wrap items-center gap-2 border-t bg-background px-1 py-2">
      <span className="text-sm font-medium">{items.length} selected</span>
      <Select disabled={busy} onValueChange={(value) => void apply({ state_id: value })}>
        <SelectTrigger className="h-8 w-[140px]">
          <SelectValue placeholder="State" />
        </SelectTrigger>
        <SelectContent>
          {states.map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        disabled={busy}
        onValueChange={(value) => {
          if (value === '__none__') void apply({ assignee_ids: [] });
          else void apply({ assignee_ids: [value] });
        }}
      >
        <SelectTrigger className="h-8 w-[160px]">
          <SelectValue placeholder="Assignee" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Unassigned</SelectItem>
          {members.map((m) => (
            <SelectItem key={m.user_id} value={m.user_id}>
              {m.email ?? m.user_id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        disabled={busy}
        onValueChange={(value) => {
          if (value === '__none__') void apply({ label_ids: [] });
          else void apply({ label_ids: [value] });
        }}
      >
        <SelectTrigger className="h-8 w-[140px]">
          <SelectValue placeholder="Label" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">No labels</SelectItem>
          {labels.map((l) => (
            <SelectItem key={l.id} value={l.id}>
              {l.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void archive()}>
        Mark done
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
